import { Link } from "@tanstack/react-router";
import { RatingStrip } from "../design/RatingStrip.js";
import styles from "./Dashboard.module.css";

/**
 * The dashboard (design/README.md §7): four things, in this order, and no charts.
 *
 * The rule that shapes it: **if a panel has no data yet it is absent, not empty.** A fresh library
 * would otherwise be four boxes of noughts and three headings explaining that there is nothing
 * under them — which is a worse first impression than no dashboard at all. The rate panel is the
 * exception and stays, because on an empty library it is the one thing there is to do.
 */

export interface DashboardSong {
  id: number;
  title: string;
  artist: string;
  /** The artist is a destination, not a caption — see the recent rows. */
  artistId: number;
  album: string;
  albumId: number;
  score: number;
  ratedAt?: string;
  art: string | null;
}

export interface DashboardProps {
  queue: { needToRate: number; ratedThisWeek: number; longestSitting: number };
  recent: DashboardSong[];
  top: DashboardSong[];
  counters: { songsRated: number; albumsComplete: number; qualifiedArtists: number; changesThisMonth: number };
  onRate: () => void;
}

/** `3 DAYS AGO`. Relative because the dashboard is about momentum, not filing. */
function ago(iso: string | undefined): string {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "TODAY";
  if (days === 1) return "YESTERDAY";
  if (days < 7) return `${days} DAYS AGO`;
  if (days < 14) return "LAST WEEK";
  if (days < 60) return `${Math.floor(days / 7)} WEEKS AGO`;
  return `${Math.floor(days / 30)} MONTHS AGO`;
}

export function Dashboard({ queue, recent, top, counters, onRate }: DashboardProps) {
  return (
    <div className={styles.page}>
      {/* 1. The rate action. Always present: on an empty library it is the only thing to do. */}
      <section className={styles.rate} data-testid="rate-panel">
        {/* The numeral had NEED TO RATE set above it and SONGS AWAITING RATING under it: one fact
            labelled twice, in two registers, around a number that is already unmistakable. The
            sentence under the numeral is the label, and the numeral is the subject of it. */}
        <div className={styles.count} data-testid="need-to-rate">
          {queue.needToRate.toLocaleString()}
        </div>
        <div className={styles.rateTitle}>Songs awaiting rating</div>
        <div className={styles.rateLine}>
          PRESS R FROM ANYWHERE · {queue.ratedThisWeek.toLocaleString()} RATED THIS WEEK
          {/* Omitted rather than shown as zero: "longest sitting 0" is a sentence about nothing. */}
          {queue.longestSitting > 0 && ` · LONGEST SITTING ${queue.longestSitting.toLocaleString()}`}
        </div>
        {/* One action. "See what is waiting" and "Same song?" both stood beside it, and both are
            rows in the rail — the panel exists to start the sitting, and a panel offering three
            ways out of itself is not doing that. */}
        <div className={styles.rateActions}>
          <button type="button" className={styles.ratePrimary} data-testid="continue-queue" onClick={onRate}>
            Continue queue →
          </button>
        </div>
      </section>

      {/* 2. Recently rated — absent when nothing has been. */}
      {recent.length > 0 && (
        <section className={styles.section} data-testid="recent-panel">
          <h2 className={styles.sectionTitle}>Recently rated</h2>
          <div className={styles.recent}>
            {recent.map((song) => (
              /*
               * Two destinations, each on the thing it names — the same rule the rankings table
               * follows. The row used to be one link to the album, so clicking the words "The
               * Flaming Lips" landed you on Yoshimi: the one word on the row that is an artist
               * was the one word that could not take you to them.
               */
              <div key={song.id} className={styles.recentRow} data-testid="recent-row">
                <Link
                  to="/album/$albumId"
                  params={{ albumId: String(song.albumId) }}
                  aria-label={`Go to ${song.album}`}
                  className={styles.recentCover}
                  data-testid="recent-album-link"
                >
                  {song.art ? (
                    <img className={styles.recentArt} src={song.art} alt="" />
                  ) : (
                    <div className={styles.recentArtFallback}>{song.album.slice(0, 10)}</div>
                  )}
                </Link>
                <div className={styles.recentBody}>
                  <div className={styles.recentTitle}>{song.title}</div>
                  <Link
                    to="/artist/$artistId"
                    params={{ artistId: String(song.artistId) }}
                    className={styles.recentArtist}
                    data-testid="recent-artist-link"
                  >
                    {song.artist}
                  </Link>
                  <RatingStrip value={song.score} size="display" />
                </div>
                <div className={styles.recentScore}>{song.score}</div>
                <div className={styles.recentWhen}>{ago(song.ratedAt)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 3. Top ten — absent until there is something to top. */}
      {top.length > 0 && (
        <section className={styles.section} data-testid="top-panel">
          <h2 className={styles.sectionTitle}>Top ten songs</h2>
          <div className={styles.top}>
            {top.map((song, i) => (
              <Link
                key={song.id}
                to="/album/$albumId"
                params={{ albumId: String(song.albumId) }}
                className={styles.topRow}
                data-testid="top-row"
              >
                <span className={styles.topRank}>{i + 1}</span>
                <span className={styles.topTitle}>{song.title}</span>
                <span className={styles.topArtist}>{song.artist}</span>
                <span className={styles.topScore}>{song.score}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 4. Four counters. */}
      <section className={styles.section} data-testid="counters">
        <div className={styles.counters}>
          {[
            ["Songs rated", counters.songsRated],
            ["Albums completed", counters.albumsComplete],
            ["Qualified artists", counters.qualifiedArtists],
            ["Rating changes this month", counters.changesThisMonth],
          ].map(([label, value]) => (
            <div key={label as string} className={styles.counter} data-testid="counter">
              <div className={styles.counterValue}>{(value as number).toLocaleString()}</div>
              <div className={styles.counterLabel}>{(label as string).toUpperCase()}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
